<template>
  <div class="guest-registration-page" :style="pageStyle">
    <div class="registration-container">
      <!-- Logo -->
      <div class="registration-logo">
        <img 
          v-if="orgBranding.logo_url" 
          :src="orgBranding.logo_url" 
          :alt="portalTitle"
          class="org-logo"
        />
        <h2 v-else>{{ portalTitle }}</h2>
      </div>

      <!-- Success State -->
      <div v-if="accountCreated" class="success-state">
        <div class="success-icon">✅</div>
        <h2>Account Created Successfully!</h2>
        <p class="success-message">
          Welcome, {{ firstName }}! Your account has been created.
        </p>
        <p class="guest-info">
          You're registered as a <strong>Guest User</strong>.
          {{ successMemberMessage }}
        </p>
        <div class="success-actions">
          <router-link 
            :to="loginPath"
            class="btn btn-primary"
          >
            Log In Now
          </router-link>
          <a 
            v-if="!isVmaeDemo && scavJoinUrl"
            :href="scavJoinUrl"
            target="_blank"
            class="btn btn-secondary"
          >
            Join SCAV for 40% Off
          </a>
        </div>
      </div>

      <!-- Registration Form -->
      <div v-else class="registration-form-container">
        <h2>Create Your Account</h2>
        <p class="subtitle">{{ registrationSubtitle }}</p>

        <!-- Member Check Info -->
        <div class="info-box">
          <i class="fas fa-info-circle"></i>
          <div>
            <strong>{{ memberBenefitLabel }}:</strong> {{ memberBenefitText }}
          </div>
        </div>

        <form @submit.prevent="handleRegister" class="registration-form">
          <div class="form-group">
            <label for="email">Email Address *</label>
            <input
              type="email"
              id="email"
              v-model="formData.email"
              required
              placeholder="your.email@example.com"
              :disabled="loading"
            />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                v-model="formData.first_name"
                required
                placeholder="John"
                :disabled="loading"
              />
            </div>

            <div class="form-group">
              <label for="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                v-model="formData.last_name"
                required
                placeholder="Doe"
                :disabled="loading"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="password">Password *</label>
            <input
              type="password"
              id="password"
              v-model="formData.password"
              required
              placeholder="At least 8 characters"
              minlength="8"
              :disabled="loading"
            />
            <small class="form-hint">Must be at least 8 characters</small>
          </div>

          <div class="form-group">
            <label for="confirmPassword">Confirm Password *</label>
            <input
              type="password"
              id="confirmPassword"
              v-model="formData.confirmPassword"
              required
              placeholder="Re-enter your password"
              :disabled="loading"
            />
          </div>

          <!-- Error Message -->
          <div v-if="error" class="error-message">
            <i class="fas fa-exclamation-circle"></i>
            {{ error }}
          </div>

          <!-- Submit Button -->
          <button 
            type="submit" 
            class="btn btn-primary btn-block"
            :disabled="loading"
          >
            <span v-if="loading">
              <i class="fas fa-spinner fa-spin"></i>
              Creating Account...
            </span>
            <span v-else>
              Create Account
            </span>
          </button>
        </form>

        <!-- Login Link -->
        <div class="login-link">
          Already have an account?
          <router-link :to="loginPath">Log In</router-link>
        </div>

        <!-- SCAV Member Link -->
        <div v-if="!isVmaeDemo" class="scav-member-link">
          <p>
            <strong>SCAV Member?</strong> You may need to verify your membership.
            <router-link to="/register-scav">Verify SCAV Membership</router-link>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import {
  isVmaeDemoOrgSlug,
  loginPathForOrg,
  resolveOrgSlugFromHost,
} from '@/utils/associationPortal'

export default {
  name: 'RegisterGuest',
  data() {
    return {
      formData: {
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        confirmPassword: ''
      },
      loading: false,
      error: null,
      accountCreated: false,
      firstName: '',
      orgBranding: {},
      organizationSlug: 'colibri-media-group',
      scavJoinUrl: 'https://scav.memberclicks.net/index.php?option=com_mcform&view=ngforms&id=2056696#/',
    }
  },
  computed: {
    isFormValid() {
      return (
        this.formData.email &&
        this.formData.first_name &&
        this.formData.last_name &&
        this.formData.password.length >= 8 &&
        this.formData.password === this.formData.confirmPassword
      )
    },
    isVmaeDemo() {
      return isVmaeDemoOrgSlug(this.organizationSlug)
    },
    portalTitle() {
      return this.orgBranding.app_name || 'Member Portal'
    },
    loginPath() {
      return loginPathForOrg(this.organizationSlug)
    },
    registrationSubtitle() {
      if (this.isVmaeDemo) {
        return `Get started with ${this.portalTitle}`
      }
      return 'Get started with Vet Media eLearning'
    },
    memberBenefitLabel() {
      return this.orgBranding.association_meta?.short_name
        ? `${this.orgBranding.association_meta.short_name} Members`
        : 'SCAV Members'
    },
    memberBenefitText() {
      if (this.isVmaeDemo) {
        return 'Verified members may receive association CE discounts and complimentary course benefits.'
      }
      return "You'll get 40% off all courses! We'll check your membership status automatically."
    },
    successMemberMessage() {
      if (this.isVmaeDemo) {
        return 'Sign in to browse CE courses and verify member benefits.'
      }
      return 'Join SCAV to unlock 40% off all courses!'
    },
    pageStyle() {
      const primary = this.orgBranding.colors?.primary || '#2c5aa0'
      const secondary = this.orgBranding.colors?.secondary || '#00a79d'
      return {
        background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`,
      }
    },
  },
  mounted() {
    this.organizationSlug = this.$route.query.org || resolveOrgSlugFromHost() || 'colibri-media-group'
    this.loadOrgBranding()
  },
  methods: {
    async loadOrgBranding() {
      try {
        const response = await api.get(`/users/organizations/${this.organizationSlug}/`)
        if (response.data.branding_settings) {
          this.orgBranding = response.data.branding_settings
        }
      } catch (error) {
        console.warn('Could not load organization branding:', error)
      }
    },

    async handleRegister() {
      console.log('🔵 handleRegister called', this.formData)
      
      // Validate passwords match
      if (this.formData.password !== this.formData.confirmPassword) {
        this.error = 'Passwords do not match'
        console.log('❌ Passwords do not match')
        return
      }

      console.log('✅ Validation passed, submitting...')
      this.loading = true
      this.error = null

      try {
        console.log('📡 Posting to /users/register-guest/')
        const response = await api.post('/users/register-guest/', {
          email: this.formData.email,
          password: this.formData.password,
          first_name: this.formData.first_name,
          last_name: this.formData.last_name,
          organization: this.organizationSlug,
        })

        console.log('✅ Response:', response.data)
        
        if (response.data.success) {
          this.accountCreated = true
          this.firstName = this.formData.first_name
        }
      } catch (error) {
        console.error('❌ Registration error:', error)
        if (error.response?.data) {
          this.error = error.response.data.error || error.response.data.message || 'Registration failed'
        } else {
          this.error = 'Unable to create account. Please try again.'
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.guest-registration-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.registration-container {
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  padding: 3rem;
  max-width: 500px;
  width: 100%;
}

.registration-logo {
  text-align: center;
  margin-bottom: 2rem;
}

.org-logo {
  max-width: 200px;
  max-height: 60px;
  object-fit: contain;
}

.registration-logo h2 {
  color: #2c5aa0;
  font-size: 1.5rem;
  margin: 0;
}

.registration-form-container h2 {
  text-align: center;
  color: #1a1a2e;
  margin-bottom: 0.5rem;
}

.subtitle {
  text-align: center;
  color: #6b7280;
  margin-bottom: 2rem;
}

.info-box {
  background: #e3f2fd;
  border-left: 4px solid #2196f3;
  padding: 1rem;
  margin-bottom: 2rem;
  border-radius: 8px;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.info-box i {
  color: #2196f3;
  font-size: 1.2rem;
  margin-top: 2px;
}

.info-box strong {
  color: #1976d2;
}

.info-box small {
  color: #666;
}

.registration-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #1a1a2e;
}

.form-group input {
  padding: 0.75rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: #00a79d;
  box-shadow: 0 0 0 3px rgba(0, 167, 157, 0.1);
}

.form-group input:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.form-hint {
  color: #6b7280;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.error-message {
  background: #fee;
  border: 1px solid #fcc;
  color: #c33;
  padding: 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn {
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-primary {
  background: #00a79d;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #008c84;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 167, 157, 0.3);
}

.btn-secondary {
  background: white;
  color: #00a79d;
  border: 2px solid #00a79d;
}

.btn-secondary:hover {
  background: #00a79d;
  color: white;
}

.btn-block {
  width: 100%;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.login-link {
  text-align: center;
  margin-top: 1.5rem;
  color: #6b7280;
}

.login-link a {
  color: #00a79d;
  text-decoration: none;
  font-weight: 600;
}

.login-link a:hover {
  text-decoration: underline;
}

.scav-member-link {
  text-align: center;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e1e5e9;
}

.scav-member-link p {
  color: #6b7280;
  font-size: 0.95rem;
  margin: 0;
}

.scav-member-link a {
  color: #2196f3;
  text-decoration: none;
  font-weight: 600;
}

.scav-member-link a:hover {
  text-decoration: underline;
}

/* Success State */
.success-state {
  text-align: center;
}

.success-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.success-state h2 {
  color: #1a1a2e;
  margin-bottom: 1rem;
}

.success-message {
  color: #4caf50;
  font-size: 1.1rem;
  margin-bottom: 1rem;
}

.guest-info {
  background: #fff3e0;
  border: 2px solid #ff9800;
  border-radius: 8px;
  padding: 1rem;
  margin: 1.5rem 0;
  color: #e65100;
}

.guest-info strong {
  color: #bf360c;
}

.success-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
}

@media (max-width: 768px) {
  .registration-container {
    padding: 2rem 1.5rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>

